import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  MoneyField,
  Notice,
  PageHeader,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import { billingKeys, billsQuery, currentShiftQuery, openShift } from '../api/billing.ts';
import { paybillPaymentsQuery } from '../api/paybill.ts';
import { can, sessionQuery } from '../api/session.ts';
import { BillPanel } from '../billing/bill-panel.tsx';
import styles from '../billing/billing.module.css';
import { money } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { CashierStayCard, InpatientStays } from '../inpatient-billing/cashier-stays.tsx';
import { outsideDialogs, screen, ShortcutHint } from '../layout/screen.tsx';
import { formatTimeOnly } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/cashier');

/**
 * The cashier (DESIGN.md §8, plan §6.2): bills owing on the left, oldest first; the chosen
 * bill on the right. The shift is always one click away. The address carries the visit's
 * internal ID only.
 */
export function CashierPage() {
  const { visitId } = route.useSearch();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const canShift = session !== null && can(session, 'cashier.shift.open');
  return (
    <div className={styles.page}>
      <PageHeader title="Cashier" {...(canShift ? { actions: <ShiftStatus /> } : {})} />
      <div className={screen.split}>
        <BillQueue selected={visitId ?? null} />
        {visitId === undefined ? (
          <NoBillOpen />
        ) : (
          <div className={styles.page}>
            <BillPanel key={visitId} visitId={visitId} />
            <CashierStayCard key={`stay-${visitId}`} visitId={visitId} />
          </div>
        )}
      </div>
    </div>
  );
}

function ShiftStatus() {
  const shift = useQuery(currentShiftQuery);
  const [opening, setOpening] = useState(false);
  if (shift.isPending) return <span>Checking your shift</span>;
  if (shift.isError)
    return <span>{errorText(shift.error, "Your shift couldn't be checked.")}</span>;
  const open = shift.data.shift;
  return (
    <div className={styles.toolbar}>
      {open === null ? (
        <>
          <span>No shift open.</span>
          <Button
            variant="primary"
            onPress={() => {
              setOpening(true);
            }}
          >
            Open shift
          </Button>
        </>
      ) : (
        <>
          <span>
            <span>Shift </span>
            <span className="wodi-id">{open.number}</span>
            <span>{` open since ${formatTimeOnly(open.openedAt)}`}</span>
          </span>
          <Link to="/cashier/shift">Shift summary</Link>
        </>
      )}
      {opening ? (
        <OpenShiftDialog
          onClose={() => {
            setOpening(false);
          }}
        />
      ) : null}
    </div>
  );
}

export function OpenShiftDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [float, setFloat] = useState<bigint | null>(null);
  const [tried, setTried] = useState(false);
  const submit = useMutation({
    mutationFn: () => openShift(float ?? 0n),
    onSuccess: async (shift) => {
      await queryClient.invalidateQueries({ queryKey: billingKeys.all });
      toasts.show({ title: 'Shift opened', description: shift.number });
      onClose();
    },
  });
  const go = () => {
    setTried(true);
    if (float !== null && float >= 0n) submit.mutate();
  };
  useShortcut('mod+enter', go, { allowInFields: true, description: 'Open shift' });
  return (
    <Dialog
      title="Open shift"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <MoneyField
          label="Opening float"
          description="The cash in the drawer now, before any payment."
          value={float}
          onChange={(value) => {
            setFloat(value);
          }}
          autoFocus
          {...(tried && float === null ? { errorMessage: 'Enter the float, or 0.' } : {})}
        />
        {submit.isError ? (
          <Notice tone="error">{errorText(submit.error, "The shift wasn't opened.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={go}
          isPending={submit.isPending}
          pendingLabel="Opening"
          isDisabled={!online}
        >
          Open shift
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * The right-hand card while no bill is chosen (DESIGN.md §7, empty states): what goes here
 * and the one action to start, the same as N on the queue.
 */
function NoBillOpen() {
  const bills = useQuery(billsQuery('owing'));
  const navigate = useNavigate();
  const oldest = bills.data?.items[0];
  const nothingOwed = bills.isSuccess && oldest === undefined;
  return (
    <section className={`${styles.section} ${screen.panel}`} aria-labelledby="no-bill-heading">
      <h2 id="no-bill-heading" className={styles.sectionHeading}>
        No bill open
      </h2>
      {nothingOwed ? (
        <EmptyState>
          Bills appear on the left when a patient is charged. You can also open one from a patient's
          visits.
        </EmptyState>
      ) : (
        <EmptyState
          action={
            oldest === undefined ? undefined : (
              <Button
                variant="primary"
                shortcut="n"
                onPress={() => {
                  void navigate({ to: '/cashier', search: { visitId: oldest.visitId } });
                }}
              >
                Open the oldest bill
              </Button>
            )
          }
        >
          Choose a bill on the left, or open one from a patient's visits.
        </EmptyState>
      )}
    </section>
  );
}

function BillQueue({ selected }: { selected: string | null }) {
  const bills = useQuery({ ...billsQuery('owing'), refetchInterval: 15_000 });
  const navigate = useNavigate();
  const oldest = bills.data?.items[0];
  // N opens the oldest bill, as on the other queues (DESIGN.md §6.3).
  useShortcut(
    'n',
    outsideDialogs(() => {
      if (oldest !== undefined)
        void navigate({ to: '/cashier', search: { visitId: oldest.visitId } });
    }),
    { enabled: oldest !== undefined, description: 'Open the oldest bill' },
  );
  return (
    <nav aria-label="Bills waiting" className={`${styles.section} ${screen.panel}`}>
      <div className={styles.queueHeader}>
        <h2 className={styles.sectionHeading}>Bills waiting</h2>
        {bills.isSuccess && bills.data.items.length > 0 ? (
          <span className={styles.meta}>
            {bills.data.items.length === 1 ? '1 bill' : `${String(bills.data.items.length)} bills`}
          </span>
        ) : null}
      </div>
      {bills.isPending ? <LoadingBlock label="Loading bills" rows={4} /> : null}
      {bills.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void bills.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(bills.error, "Bills couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {bills.isSuccess && bills.data.items.length === 0 ? (
        <EmptyState>Nothing is owed here right now.</EmptyState>
      ) : null}
      {bills.isSuccess ? (
        <div className={styles.queue}>
          {bills.data.items.map((b) => (
            <Link
              key={b.visitId}
              to="/cashier"
              search={{ visitId: b.visitId }}
              className={styles.queueItem}
              {...(selected === b.visitId ? { 'aria-current': 'page' as const } : {})}
            >
              <span className={styles.queueLine}>
                <strong>{b.patient.name}</strong>
                <span className="wodi-num">{money(b.owed)}</span>
              </span>
              <span className={`${styles.queueLine} ${styles.meta}`}>
                <span className="wodi-id">{b.visitNumber}</span>
                <span>
                  {b.unpricedCount > 0
                    ? 'Has charges without a price'
                    : `Since ${formatTimeOnly(b.startedAt)}`}
                </span>
              </span>
            </Link>
          ))}
        </div>
      ) : null}
      {oldest === undefined ? null : <ShortcutHint combo="n" action="open the oldest bill" />}
      <PaybillLink />
      <InpatientStays selected={selected} />
    </nav>
  );
}

/** How many paybill payments wait for a person (ADR 0020), and the way to them. */
function PaybillLink() {
  const waiting = useQuery({ ...paybillPaymentsQuery('unmatched'), refetchInterval: 30_000 });
  const count = waiting.data?.unmatchedCount ?? 0;
  return (
    <p className={styles.meta}>
      <Link to="/cashier/paybill">Paybill payments</Link>
      {waiting.isSuccess
        ? count === 0
          ? ': none waiting.'
          : `: ${String(count)} waiting to be matched.`
        : null}
    </p>
  );
}
